import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { TextField, Typography, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "../../api/axios";
import { useToken } from "../../Hooks/useToken";
import styles from "./ResetPassword.module.css";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [userToken] = useToken();

  // Redirect if user is already logged in
  useEffect(() => {
    if (userToken) {
      navigate('/events', { replace: true });
    }
  }, [userToken, navigate]);

  // Get token and email from URL parameters
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlEmail = searchParams.get('email');
    
    if (urlToken && urlEmail) {
      setToken(urlToken);
      setEmail(urlEmail);
      validateResetToken(urlToken, urlEmail);
    } else {
      setIsValidating(false);
      enqueueSnackbar("Invalid reset link. Please request a new password reset.", { 
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  }, [searchParams]);

  // Validate the reset token
  const validateResetToken = async (token, email) => {
    try {
      // Token validation happens when user submits, but we show the form if parameters exist
      setIsTokenValid(true);
      setIsValidating(false);
    } catch (error) {
      setIsTokenValid(false);
      setIsValidating(false);
      enqueueSnackbar("Invalid or expired reset link. Please request a new password reset.", { 
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  };

  // Password validation function
  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    return minLength && hasUpperCase && hasLowerCase && hasNumber;
  };

  // Real-time validation
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'newPassword':
        if (!value || value.trim() === '') {
          newErrors.newPassword = 'Password is required';
        } else if (!validatePassword(value)) {
          newErrors.newPassword = 'Password must be at least 8 characters with uppercase, lowercase, and number';
        } else {
          delete newErrors.newPassword;
        }
        
        // Also validate confirm password if it exists
        if (confirmPassword && value !== confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else if (confirmPassword) {
          delete newErrors.confirmPassword;
        }
        break;
        
      case 'confirmPassword':
        if (!value || value.trim() === '') {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (value !== newPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid
  useEffect(() => {
    const hasPasswordError = !newPassword || !validatePassword(newPassword);
    const hasConfirmError = !confirmPassword || newPassword !== confirmPassword;
    
    const isValid = !hasPasswordError && !hasConfirmError;
    setIsFormValid(isValid);
    
    // Clear errors for valid fields
    const newErrors = { ...errors };
    if (!hasPasswordError && errors.newPassword) {
      delete newErrors.newPassword;
    }
    if (!hasConfirmError && errors.confirmPassword) {
      delete newErrors.confirmPassword;
    }
    setErrors(newErrors);
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      enqueueSnackbar("Please fix the errors in the form.", { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send password reset confirmation
      const data = {
        token: token,
        email: email,
        newPassword: newPassword
      };
      
      const response = await axios.post("/api/auth/reset-password", JSON.stringify(data), {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Show success message
      setShowSuccessMessage(true);
      enqueueSnackbar("Password reset successful! You can now log in with your new password.", { 
        variant: 'success',
        autoHideDuration: 8000
      });
      
      // Clear form
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
      setIsFormValid(false);
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error || 'Invalid or expired reset link';
        enqueueSnackbar(errorMsg + ". Please request a new password reset.", { 
          variant: 'error',
          autoHideDuration: 8000
        });
      } else if (error.response?.status === 404) {
        enqueueSnackbar("Account not found. Please check your email or create a new account.", { 
          variant: 'error',
          autoHideDuration: 8000
        });
      } else if (error.response?.status >= 500) {
        enqueueSnackbar("Server error occurred. Please try again later.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (!navigator.onLine) {
        enqueueSnackbar("No internet connection. Please check your connection and try again.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else {
        enqueueSnackbar("Failed to reset password. Please try again or request a new reset link.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <>
        <div className={styles.accountNav}>
          <Typography className={styles.navText}>
            Sharing God's Love in Word and Deed
          </Typography>
        </div>
        <div className={styles.resetPasswordMain}>
          <div className={styles.contentHalf}>
            <section className={styles.resetPasswordFormSection}>
              <img
                src="./Images/baptists-on-mission-logo.png"
                alt="Baptists on Mission Logo"
                className={styles.bomLogo}
              />
              <Typography variant="h5">Validating reset link...</Typography>
            </section>
          </div>
          <img
            className={styles.resetPasswordImg}
            src="./Images/sign-in-page-image.jpg"
            alt="Baptists on Mission reset password"
          />
        </div>
      </>
    );
  }

  // Show error if token is invalid
  if (!isTokenValid) {
    return (
      <>
        <div className={styles.accountNav}>
          <Typography className={styles.navText}>
            Sharing God's Love in Word and Deed
          </Typography>
        </div>
        <div className={styles.resetPasswordMain}>
          <div className={styles.contentHalf}>
            <section className={styles.resetPasswordFormSection}>
              <img
                src="./Images/baptists-on-mission-logo.png"
                alt="Baptists on Mission Logo"
                className={styles.bomLogo}
              />
              <div className={styles.formHeader}>
                <Typography variant="h4" className={styles.title}>
                  Invalid Reset Link
                </Typography>
                <Typography variant="body1" className={styles.subtitle}>
                  This password reset link is invalid or has expired. Reset links are only valid for 1 hour.
                </Typography>
              </div>
              <div className={styles.authLinks}>
                <p className="mb-3">
                  <Link to="/forgot-password">Request New Reset Link</Link>
                </p>
                <p className="mb-3">
                  <Link to="/login">← Back to Sign In</Link>
                </p>
              </div>
            </section>
          </div>
          <img
            className={styles.resetPasswordImg}
            src="./Images/sign-in-page-image.jpg"
            alt="Baptists on Mission reset password"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>
          Sharing God's Love in Word and Deed
        </Typography>
      </div>
      <div className={styles.resetPasswordMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.resetPasswordFormSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />
            <div className={styles.formHeader}>
              <Typography variant="h4" className={styles.title}>
                Reset Your Password
              </Typography>
              <Typography variant="body1" className={styles.subtitle}>
                Enter your new password below. Make sure it's strong and secure.
              </Typography>
            </div>
            
            {!showSuccessMessage ? (
              <form onSubmit={handleSubmit} className={styles.resetPasswordForm}>
                <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                    Email Address
                  </Typography>
                  <TextField
                    type="email"
                    id="email"
                    value={email}
                    disabled
                    fullWidth
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                    New Password
                  </Typography>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      validateField('newPassword', e.target.value);
                    }}
                    error={!!errors.newPassword}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.newPassword && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.newPassword}
                    </Typography>
                  )}
                  <Typography variant="caption" className={styles.helperText}>
                    Password must be at least 8 characters with uppercase, lowercase, and number
                  </Typography>
                </div>
                
                <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                    Confirm New Password
                  </Typography>
                  <TextField
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      validateField('confirmPassword', e.target.value);
                    }}
                    error={!!errors.confirmPassword}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.confirmPassword && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.confirmPassword}
                    </Typography>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="button-default button-full-width"
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>
            ) : (
              <div className={styles.successMessage}>
                <Typography variant="h6" className={styles.successTitle}>
                  ✅ Password Reset Successful!
                </Typography>
                <Typography variant="body2" className={styles.successText}>
                  Your password has been successfully reset. You can now log in with your new password.
                </Typography>
                <Typography variant="caption" className={styles.redirectText}>
                  Redirecting to login page in 3 seconds...
                </Typography>
              </div>
            )}
            
            <div className={styles.authLinks}>
              <p className="mb-3">
                <Link to="/login">← Back to Sign In</Link>
              </p>
            </div>
            
            <p className="problem-text">
              If you have any problems, please contact us at (800) 395-5102 ext 5596
            </p>
          </section>
        </div>
        <img
          className={styles.resetPasswordImg}
          src="./Images/sign-in-page-image.jpg"
          alt="Baptists on Mission reset password"
        />
      </div>
    </>
  );
};

export default ResetPassword;

